import React, {type ComponentProps} from 'react';
import {CheckIcon, EyeIcon, TrashIcon} from '@heroicons/react/24/outline';
import type {MeshType} from 'api/generated';
import {format} from 'date-fns';
import {classNames, fileSize} from 'utils';
import {imgNone} from 'assets';

interface IModelItemProps extends Omit<ComponentProps<'input'>, 'onClick'> {
  mesh: MeshType;
  onClick?: (mesh: MeshType) => void;
  onRemove?: (mesh: MeshType) => void;
  onShow: (mesh: MeshType) => void;
  disabled?: boolean;
}

const ModelItem = ({mesh, onClick, onRemove, onShow, disabled = false, ...inputProps}: IModelItemProps) => {
  const handleShow = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    onShow(mesh);
  };

  const interactable = !!onClick && !disabled;

  return (
    <li
      className={classNames(
        'relative duration-300 rounded-lg p-1.5',
        disabled && 'opacity-50',
        interactable && 'hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-90 cursor-pointer',
      )}
      onClick={() => interactable && onClick(mesh)}>
      {interactable && (
        <div className="absolute left-2.5 top-2.5 z-10 group grid size-4 grid-cols-1">
          <input
            type="checkbox"
            className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-400 bg-white checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
            {...inputProps}
          />
          <CheckIcon className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25" />
        </div>
      )}

      <div className="relative group overflow-hidden rounded-lg bg-gray-100">
        <img
          src={mesh.imageUrl ?? imgNone}
          alt={mesh.name}
          className={classNames('pointer-events-none aspect-square object-cover', interactable && 'group-hover:opacity-75')}
        />
        <button type="button" className="absolute inset-0 focus:outline-hidden">
          <span className="sr-only">View details for {mesh.name}</span>
        </button>
        <button
          type="button"
          className="hidden group-hover:block absolute right-0 bottom-0 bg-primary text-white rounded-lg py-0.5 px-1 cursor-zoom-in"
          onClick={handleShow}>
          <EyeIcon className="size-4" />
        </button>
        {!!onRemove && (
          <button
            type="button"
            className="hidden group-hover:block absolute right-0 top-0 bg-red-500 text-white rounded-lg p-1 cursor-pointer"
            onClick={() => onRemove(mesh)}>
            <TrashIcon className="size-4" />
          </button>
        )}
      </div>
      <p
        title={`${mesh.name}.${mesh.fileFormat}`}
        className="pointer-events-none mt-2 block truncate text-xs font-medium text-gray-900 dark:text-white">
        {mesh.name}.{mesh.fileFormat}
      </p>
      <p className="pointer-events-none block truncate text-xs font-medium text-gray-500 dark:text-darkText">
        {format(new Date(mesh.createdAt!), 'd MMM y')}
      </p>
      <p className="pointer-events-none block text-xs font-medium text-gray-500 dark:text-darkText">{fileSize(mesh.byteSize)}</p>
    </li>
  );
};

export default React.memo(ModelItem);
